import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

function CouponForm() {
  const { couponId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    is_active: true
  });

  const isEditing = Boolean(couponId);

  useEffect(() => {
    if (couponId) {
      loadCoupon();
    }
  }, [couponId]);

  const loadCoupon = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ecommerce/coupons/${couponId}`);
      const coupon = response.data;
      setFormData({
        code: coupon.code || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_value: coupon.discount_value || '',
        max_uses: coupon.max_uses || '',
        valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
        valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
        is_active: coupon.is_active !== false
      });
      setError('');
    } catch (err) {
      console.error('Failed to load coupon:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to load coupon';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      if (isEditing) {
        await api.put(`/ecommerce/coupons/${couponId}`, formData);
      } else {
        await api.post('/ecommerce/coupons', formData);
      }
      
      navigate('/coupons');
    } catch (err) {
      console.error('Failed to save coupon:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to save coupon';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/ecommerce/coupons/${couponId}`);
      navigate('/coupons');
    } catch (err) {
      console.error('Failed to delete coupon:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to delete coupon';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/coupons');
  };

  if (loading && isEditing && !formData.code) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <button onClick={() => navigate('/coupons')} className="breadcrumb-link">Coupons</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{isEditing ? 'Edit' : 'New'}</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header with Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              🎟️ {isEditing ? 'Edit Coupon' : 'Create Coupon'}
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              {isEditing ? 'Update coupon information' : 'Add a new discount coupon'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {isEditing && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
            <button
              onClick={handleCancel}
              disabled={loading}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Saving...' : isEditing ? '💾 Update' : '➕ Create'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError('')}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              Basic Information
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                Coupon Code *
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                placeholder="e.g., SUMMER2024, SAVE20"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', textTransform: 'uppercase' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Discount Type *
                </label>
                <select
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Discount Value *
                </label>
                <input
                  type="number"
                  name="discount_value"
                  value={formData.discount_value}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder={formData.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 10.00'}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>
            </div>
          </div>

          {/* Usage & Validity Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              Usage & Validity
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                Maximum Uses
              </label>
              <input
                type="number"
                name="max_uses"
                value={formData.max_uses}
                onChange={handleChange}
                min="0"
                placeholder="Leave empty for unlimited"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
              />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                Number of times this coupon can be used. Leave blank for unlimited usage.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Valid From
                </label>
                <input
                  type="date"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Valid Until
                </label>
                <input
                  type="date"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              Status
            </h3>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Active</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                (Coupon can be used by customers)
              </span>
            </label>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CouponForm;
